import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { setRange } from './features/ui/uiSlice';
import { restoreSession, selectIsAuthenticated, selectRestoring } from './features/auth/authSlice';
import { getAuthToken } from './services/apiClient';
import LoginPage from './pages/auth/LoginPage';
import SessionSplash from './pages/auth/SessionSplash';
import { CHMAP } from './constants/channels';
import { useSummaryQuery } from './services/api';
import { inr, dfmt } from './utils/format';
import AppLayout from './components/layout/AppLayout';
import Topbar, { StatPill } from './components/layout/Topbar';
import { PillSelect } from './components/common/FormField';
import { ShimmerText } from './components/common/Shimmer';
import { usePermissions } from './features/auth/permissions';
import { NoAccess } from './components/common/Access';
import OverallPage from './pages/overall/OverallPage';
import ChannelPage from './pages/channel/ChannelPage';
import UsersPage from './pages/settings/UsersPage';
import InfluencersPage from './pages/settings/InfluencersPage';
import OptionsPage from './pages/settings/OptionsPage';

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
];

export default function App() {
  const dispatch = useAppDispatch();
  const { tab, range } = useAppSelector((s) => s.ui);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const restoring = useAppSelector(selectRestoring);
  const { canAccess, isAdmin } = usePermissions();
 
  // Shares a cache key with the dashboards, so this adds no request of its own.
  // Declared before the early returns — hooks must run in the same order every render.
  const { data: summary } = useSummaryQuery({ range }, { skip: !isAuthenticated });

  // A token in storage only means we *were* signed in. Ask the API who that is —
  // the account may have been disabled or removed since.
  useEffect(() => {
    if (getAuthToken() && !isAuthenticated) dispatch(restoreSession());
  }, [dispatch, isAuthenticated]);

  if (restoring) return <SessionSplash />;
  if (!isAuthenticated) return <LoginPage />;

  const today = new Date().toISOString().slice(0, 10);
  const dateRange = summary ? `${dfmt(summary.from)} → ${dfmt(today)}` : <ShimmerText chars={16} height={10} />;
  const overallRevenue = summary ? inr(summary.current.rev) : <ShimmerText chars={8} height={17} />;
  const rangeSelect = (
    <PillSelect value={range} onChange={(e) => dispatch(setRange(e.target.value))} options={RANGE_OPTIONS} />
  );

  const channel = CHMAP[tab];
  let head;
       
  if (tab === 'overall') {
    head = {
      crumbs: <>TrustLab / <b>Diagnostic Lab</b> / Dashboard</>,
      title: 'Dashboard',
      subtitle: <>All revenue channels · {dateRange}</>,
      actions: (
        <>
          <StatPill live label={`Revenue · ${range}d`} value={overallRevenue} />
          {rangeSelect}
        </>
      ),
      body: <OverallPage />,
    }; 
  } else if (channel && !canAccess(tab)) {
    head = {
      crumbs: <>TrustLab / Channels / <b>{channel.name}</b></>,
      title: channel.name,
      subtitle: <>{channel.full}</>,
      actions: null,
      body: <NoAccess channel={channel} />,
    };
  } else if (channel) {
    const row = (summary?.byChannel || []).find((c) => c.channel === tab);
    const channelRevenue = summary ? inr(row?.rev || 0) : <ShimmerText chars={8} height={17} />;
    head = {
      crumbs: <>TrustLab / Channels / <b>{channel.name}</b></>,
      title: channel.name,
      subtitle: <>{channel.full} · {dateRange}</>,
      actions: (
        <>
          <StatPill live label={`Revenue · ${range}d`} value={channelRevenue} />
          {rangeSelect}
        </>
      ),
      body: <ChannelPage channelId={tab} />,
    };
  } else if (['users', 'influencers', 'options'].includes(tab) && !isAdmin) {
    head = {
      crumbs: <>TrustLab / <b>Settings</b></>,
      title: 'Settings',
      subtitle: 'Admin only',
      actions: null,
      body: <NoAccess area="Settings" />,
    };
  } else if (tab === 'users') {
    head = {
      crumbs: <>TrustLab / Settings / <b>Users</b></>,
      title: 'Users',
      subtitle: 'Team access & roles',
      // the page carries its own "Add User" above the table; a second one here was a duplicate
      actions: null,
      body: <UsersPage />,
    };
  } else if (tab === 'influencers') {
    head = {
      crumbs: <>TrustLab / Settings / <b>Influencer Shares</b></>,
      title: 'Influencer Shares',
      subtitle: 'Incremental commission tiers per channel',
      actions: rangeSelect,
      body: <InfluencersPage />,
    };
  } else {
    head = {
      crumbs: <>TrustLab / Settings / <b>Dropdowns</b></>,
      title: 'Dropdown Options',
      subtitle: 'Manage the options behind every dropdown in the platform',
      actions: null,
      body: <OptionsPage />,
    };
  }

  return (
    <AppLayout>
      <Topbar crumbs={head.crumbs} title={head.title} subtitle={head.subtitle} actions={head.actions} />
      {head.body}
    </AppLayout>
  );
}
